import type { ReactNode, PropsWithChildren } from 'react';
import { useRef, useState } from 'react';

import BpkBreakpoint, {
  BREAKPOINTS,
} from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import BpkButton from '@skyscanner-internal/backpack-web/bpk-component-button';
import { withButtonAlignment } from '@skyscanner-internal/backpack-web/bpk-component-icon';
import InformationCircleIconSm from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/information-circle';
import BpkImage from '@skyscanner-internal/backpack-web/bpk-component-image';
import BpkLink from '@skyscanner-internal/backpack-web/bpk-component-link';
import BpkMobileScrollContainer from '@skyscanner-internal/backpack-web/bpk-component-mobile-scroll-container';
import BpkModal from '@skyscanner-internal/backpack-web/bpk-component-modal';
import BpkSectionHeader from '@skyscanner-internal/backpack-web/bpk-component-section-header';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import BpkTooltip from '@skyscanner-internal/backpack-web/bpk-component-tooltip';

import { textColors } from '@skyscanner/bpk-foundations-web/tokens/base.es6';

import { useMBDContext } from '../common/MBD/useMBDContext';

import type {
  SortingLinksProps,
  SortingLinksSupportedIcon,
  ToolTip,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/SortingLinkts';

import STYLES from './SortingLinks.module.scss';

const ICON_MAP: Record<SortingLinksSupportedIcon, ReactNode> = {
  info: InformationCircleIconSm,
};

const MobileTooltip = ({
  acceptButtonLabel,
  children,
  descriptions,
  imgUrl,
  title,
}: {
  descriptions: ToolTip['descriptions'];
  title: ToolTip['title'];
  imgUrl: ToolTip['imgUrl'];
  acceptButtonLabel: ToolTip['acceptButtonLabel'];
  children: (onClick: Function) => ReactNode;
}) => {
  const [modalOpened, setModalOpened] = useState(false);

  return (
    <div id="sorting-links-modal-container">
      {children(() => setModalOpened(!modalOpened))}
      <BpkModal
        id={title}
        isOpen={modalOpened}
        getApplicationElement={() => document.getElementById('icon-wrap')}
        onClose={() => setModalOpened(false)}
        className={STYLES.tooltipmobileModal}
      >
        <div className={STYLES.mobileModal}>
          <BpkImage altText="" src={imgUrl} aspectRatio={1.432} />
          <div className={STYLES.paragraphWrap}>
            <BpkText
              tagName="h5"
              className={STYLES.header}
              textStyle={TEXT_STYLES.heading5}
            >
              {title}
            </BpkText>
            {descriptions.map((description) => (
              <BpkText
                tagName="p"
                textStyle={TEXT_STYLES.footnote}
                key={description}
              >
                {description}
              </BpkText>
            ))}
          </div>
          <div className={STYLES.buttonWrap}>
            <BpkButton
              className={STYLES.exitButton}
              size="large"
              onClick={() => setModalOpened(false)}
            >
              {acceptButtonLabel}
            </BpkButton>
          </div>
        </div>
      </BpkModal>
    </div>
  );
};

const Tooltip = ({
  children = null,
  descriptions,
  title,
}: PropsWithChildren<{
  descriptions: ToolTip['descriptions'];
  title: ToolTip['title'];
}>) => {
  const iconRef = useRef(null);
  return (
    <BpkTooltip
      id="best-tooltip"
      ariaLabel={title}
      target={
        <span ref={iconRef} role="tooltip" data-testid="best-tooltip-wrapper">
          {children}
        </span>
      }
    >
      <BpkText
        tagName="h5"
        className={STYLES.tooltipHeader}
        textStyle={TEXT_STYLES.heading5}
      >
        {title}
      </BpkText>
      <div className={STYLES.tooltipParagraph}>
        {descriptions.map((description) => (
          <BpkText
            tagName="p"
            textStyle={TEXT_STYLES.footnote}
            key={description}
          >
            {description}
          </BpkText>
        ))}
      </div>
    </BpkTooltip>
  );
};

const SortingLinks = ({
  h2Content,
  links,
  sortingTitle,
}: SortingLinksProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const mbdComponentName = `${rawComponentName}_sort`;

  const getSortingLinkIcon = (
    iconName?: keyof typeof ICON_MAP,
    toolTipData?: ToolTip,
  ) => {
    if (!iconName) {
      return null;
    }

    const Icon = withButtonAlignment(ICON_MAP[iconName]);

    if (toolTipData) {
      const { acceptButtonLabel, descriptions, imgUrl, title } = toolTipData;
      return (
        <BpkBreakpoint query={BREAKPOINTS.MOBILE}>
          {(isMobile: boolean) =>
            isMobile ? (
              <MobileTooltip
                title={title}
                descriptions={descriptions}
                imgUrl={imgUrl}
                acceptButtonLabel={acceptButtonLabel}
              >
                {(onClick) => (
                  <Icon
                    fill={textColors.textLinkDay}
                    onClick={onClick}
                    data-testid="best-tooltip-wrapper-mobile"
                  />
                )}
              </MobileTooltip>
            ) : (
              <Tooltip title={title} descriptions={descriptions}>
                <Icon fill={textColors.textLinkDay} />
              </Tooltip>
            )
          }
        </BpkBreakpoint>
      );
    }
    return <Icon fill={textColors.textLinkDay} />;
  };

  return (
    <BpkMobileScrollContainer
      trailingIndicatorClassName={STYLES.Indicator}
      leadingIndicatorClassName={STYLES.Indicator}
    >
      <div data-testid="sorting-links-link">
        {h2Content && (
          <div className={STYLES.section}>
            <BpkSectionHeader title={h2Content} />
          </div>
        )}

        <div className={STYLES.SortingLinks}>
          <BpkText className={STYLES.text}>{sortingTitle}</BpkText>

          {links.map(({ iconName, text, toolTip, url }, index) => (
            <div className={STYLES.link} key={text}>
              <BpkLink
                href={url}
                rel="nofollow"
                data-tracking-element-id={`${mbdComponentName}_desktop_${index}`}
                data-tracking-common-params={buildMBDParam(mbdComponentName, {
                  sort_name: text.slice(0, 40),
                })}
              >
                <BpkText
                  textStyle={TEXT_STYLES.bodyDefault}
                  className={STYLES.linkText}
                >
                  {text}
                </BpkText>
              </BpkLink>
              {getSortingLinkIcon(iconName, toolTip)}
            </div>
          ))}
        </div>
      </div>
    </BpkMobileScrollContainer>
  );
};

export default SortingLinks;
